/**
 * Hugin's advice on screen (issue #131): the box under a Varsler entry, the note after a change and
 * the unprompted suggestions above the week. The words and the choices come from advice.ts; a
 * choice is pressed by the manager and goes through a route the page already calls, nothing else.
 */
import { type ApiAbsence, useUser } from '@/api/data';
import { T } from '@/i18n';
import { deductionLine, useDeductionState } from '@/pages/fravaer/Deduction';
import { oslo } from '@/rules';
import { Button, Tip } from '@/ui';
import { fullName, num } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { useState } from 'react';
import hb from './HuginBar.module.css';
import type { Advice, Apply, Choice, Evaluation, Nudge } from './advice';

export type OnChoice = (a: Apply) => Promise<unknown> | unknown;

/** The options, one button each, its cost under it. One press at a time. */
export function Choices({ choices, onChoice }: { choices: Choice[]; onChoice: OnChoice }) {
  const [busy, setBusy] = useState(false);
  if (!choices.length) return null;
  return (
    <>
      {choices.map((c) => (
        <div className="vchoice" key={c.key} data-testid="vchoice">
          <span>
            {c.lines.map((l) => (
              <small key={l}>{l}</small>
            ))}
          </span>
          <Button
            small
            {...(c.safe ? { tone: 'pri' as const } : {})}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onChoice(c.apply);
              } finally {
                setBusy(false);
              }
            }}
          >
            {c.label}
          </Button>
        </div>
      ))}
    </>
  );
}

/**
 * A holiday asked for, against what is left: the server's count of the days it would take (the same
 * line the absence drawer shows) and the balance on the personnel record, which reaches a reader
 * with people.manage only. Without the balance the count alone is said.
 */
export function HolidayLine({ a }: { a: ApiAbsence }) {
  // a pending request is counted as approving it now would count it, as the absence drawer asks
  // (no request id: that reads back what was charged, and a pending one has been charged nothing)
  const deductible = !!a.absence_type?.is_deductible && a.status === 'pending';
  const state = useDeductionState(
    {
      absence_type_id: a.absence_type?.id ?? null,
      start_date: a.start_date,
      end_date: a.end_date,
    },
    deductible,
  );
  const user = useUser(deductible ? a.user_id : null).data;
  if (!deductible || state.days == null) return null;
  const year = Number((oslo(a.start_date)?.date ?? a.start_date).slice(0, 4));
  const now = new Date().getFullYear();
  const bal =
    year === now
      ? user?.vacation_days_balance
      : year === now + 1
        ? user?.next_year_vacation_days_balance
        : undefined;
  if (bal == null)
    return (
      <span className="vline" data-testid="vholiday">
        {deductionLine(state.days)}
      </span>
    );
  const left = bal - state.days;
  return (
    <span className={`vline${left < 0 ? ' warn' : ''}`} data-testid="vholiday">
      {left < 0
        ? T('Ikke nok feriedager: søknaden trekker {days}, {name} har {bal} igjen.', {
            days: num(state.days),
            bal: num(bal),
            name: a.user ? fullName(a.user) : T('den ansatte'),
          })
        : T('Saldoen går fra {now} til {left} dager.', { now: num(bal), left: num(left) })}
    </span>
  );
}

/** Hugin's word under one Varsler entry. */
export function AdviceBox({
  advice,
  absence,
  onChoice,
}: {
  advice: Advice;
  absence?: ApiAbsence | undefined;
  onChoice: OnChoice;
}) {
  return (
    <div className={`vadvice ${advice.tone}`} data-testid="vadvice">
      <span>
        <b>Hugin:</b> {advice.verdict}
      </span>
      {absence && <HolidayLine a={absence} />}
      {advice.lines.map((l) => (
        <span className="vline" key={l}>
          {l}
        </span>
      ))}
      <Choices choices={advice.choices} onChoice={onChoice} />
    </div>
  );
}

/**
 * Hugin's word on a review bar case, in the after-change bar's look (HuginBar.module.css, owner
 * 18.10): the spark, the verdict and its lines in one line beside the case's facts. A line too long
 * for the room ends in … and is whole in its tooltip; a screen reader reads it whole.
 */
export function AdviceLine({
  advice,
  absence,
  onChoice,
}: {
  advice: Advice;
  absence?: ApiAbsence | undefined;
  onChoice: OnChoice;
}) {
  const text = [advice.verdict, ...advice.lines].join(', ');
  return (
    <>
      <Tip text={text}>
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: focus shows the whole line */}
        <span className={hb.strip} data-testid="vadvice" data-tone={advice.tone} tabIndex={0}>
          <span className={hb.icon} aria-hidden="true">
            <Icon name="hugin" />
          </span>
          <span className={hb.stripText}>
            <span className="sr-only">Hugin: </span>
            {text}
            {absence && (
              <>
                {', '}
                <HolidayLine a={absence} />
              </>
            )}
          </span>
        </span>
      </Tip>
      <Choices choices={advice.choices} onChoice={onChoice} />
    </>
  );
}

/** What a saved change did, said once, until closed or the next change. */
/**
 * Hugin after a change (move, swap, delete, new length): a blue bar under the review bar, the same
 * shape (planning-hugin delivery «ac-strip», owner 2026-10-18). The sparkle, the result in bold, the
 * change, the week and the warnings in one small line; «Se detaljer» opens every line we have, the
 * warnings as severity chips, and Hugin's other options; the X closes it. It no longer pushes the plan
 * down by a lilac block.
 */
export function HuginEval({
  ev,
  onChoice,
  onClose,
}: {
  ev: Evaluation;
  onChoice: OnChoice;
  onClose: () => void;
}) {
  const [open, setOpen] = useState(false);
  const id = `hugin-eval-${ev.id}`;
  return (
    <output className={hb.bar} data-testid="hugin-eval" data-tone={ev.tone} aria-live="polite">
      <span className={hb.main}>
        <span className={hb.icon} aria-hidden="true">
          <Icon name="hugin" />
        </span>
        <span className={hb.text}>
          <strong data-testid="hugin-eval-head">{ev.headline}</strong>
          <small data-testid="hugin-eval-detail">{ev.detail}</small>
        </span>
        <span className={hb.actions}>
          <button
            type="button"
            className={hb.more}
            aria-expanded={open}
            aria-controls={id}
            onClick={() => setOpen(!open)}
          >
            {open ? T('Skjul detaljer') : T('Se detaljer')}
          </button>
          <button type="button" className={hb.x} aria-label={T('Lukk')} onClick={onClose}>
            <Icon name="close" />
          </button>
        </span>
      </span>
      {open && (
        <span className={hb.details} id={id} data-testid="hugin-eval-details">
          <b className={hb.title}>{ev.title}</b>
          {ev.balances.map((l) => (
            <span className={hb.line} key={l}>
              {l}
            </span>
          ))}
          {ev.added.length > 0 && (
            <span className={hb.chips}>
              {ev.added.map((a) => (
                <span
                  key={a.text}
                  className={`chip sm ${a.sev === 'd' ? 'dgr' : 'warn'}`}
                  data-testid="hugin-eval-chip"
                  data-sev={a.sev}
                >
                  <Icon name={a.sev === 'd' ? 'stop' : 'warn'} />
                  {T('Nytt varsel: {text}', { text: a.text })}
                </span>
              ))}
            </span>
          )}
          {ev.gone.length > 0 && (
            <span className={hb.chips}>
              {ev.gone.map((g) => (
                <span key={g} className="chip sm ok">
                  <Icon name="check" />
                  {T('Løst: {text}', { text: g })}
                </span>
              ))}
            </span>
          )}
          {!ev.added.length && !ev.gone.length && (
            <span className={hb.line}>{T('Ingen nye regelvarsler.')}</span>
          )}
          <Choices choices={ev.choices} onChoice={onChoice} />
        </span>
      )}
    </output>
  );
}

/** The unprompted suggestions above the week, open until the manager folds them. */
export function HuginNotes({
  list,
  onChoice,
  onOpen,
  folded = false,
}: {
  list: Nudge[];
  onChoice: OnChoice;
  onOpen: (n: Nudge) => void;
  /** a phone starts with the line alone, so the day stays on screen */
  folded?: boolean;
}) {
  const [open, setOpen] = useState(!folded);
  // three at first, so the week stays in view; the rest one press away
  const [all, setAll] = useState(false);
  if (!list.length) return null;
  const shown = all ? list : list.slice(0, 3);
  return (
    <details
      className="al info vhugin"
      data-testid="hugin-notes"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary>
        {list.length === 1
          ? T('Hugin ser 1 ting å se på')
          : T('Hugin ser {n} ting å se på', { n: list.length })}
      </summary>
      <ul>
        {shown.map((n) => (
          <li key={n.key} data-testid="hugin-note">
            {n.shiftId != null || n.person != null ? (
              <button type="button" className="lnk" onClick={() => onOpen(n)}>
                {n.text}
              </button>
            ) : (
              <span>{n.text}</span>
            )}
            {n.absence && <HolidayLine a={n.absence} />}
            {n.choice && <Choices choices={[n.choice]} onChoice={onChoice} />}
          </li>
        ))}
      </ul>
      {list.length > shown.length && (
        <button type="button" className="lnk" onClick={() => setAll(true)}>
          {T('Vis alle {n}', { n: list.length })}
        </button>
      )}
    </details>
  );
}
